import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const KOFI_URL = 'https://ko-fi.com/Bioandchemmasters';
const YOUTUBE_URL = 'https://youtube.com/@bioandchemmasters';

// Official YouTube glyph (filled red)
const YouTubeLogo = ({ size = 28, className = '' }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    className={className}
    style={{ color: '#FF0000' }}
  >
    <path
      fill="currentColor"
      d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.016 3.016 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136C4.495 20.455 12 20.455 12 20.455s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
    />
    <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const slides = [
  { src: '/images/pack-4.jpeg', alt: 'Maxwell-Boltzmann distribution revision page' },
  { src: '/images/pack-2.jpeg', alt: 'Splicing and translation revision page' },
  { src: '/images/pack-3.jpeg', alt: 'Exam practice questions page' },
  { src: '/images/pack-1.jpg', alt: 'Titration revision page' },
];

// ---------- Top intro with 2 toggle bubbles ----------
const TopIntro = ({ selection, setSelection }) => (
  <section className="pt-32 md:pt-36 pb-12 md:pb-16 bg-white">
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="font-display text-[44px] md:text-[64px] lg:text-[80px] leading-[1.0] tracking-tight font-semibold text-neutral-900"
      >
        2 Different Methods.<br />Same Success.
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.25 }}
        className="mt-12 flex flex-col sm:flex-row gap-3 justify-center"
      >
        {[
          { key: 'packs', label: 'Revision Packs' },
          { key: 'youtube', label: 'My YouTube' },
        ].map((opt) => {
          const isOn = selection === opt.key;
          return (
            <motion.button
              key={opt.key}
              onClick={() => setSelection(opt.key)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className={`px-6 py-2.5 rounded-full text-[13px] md:text-[14px] font-medium border transition-colors duration-300 ${
                isOn
                  ? 'bg-neutral-900 text-white border-neutral-900'
                  : 'bg-white text-neutral-900 border-neutral-300 hover:border-neutral-500'
              }`}
            >
              {opt.label}
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  </section>
);

// ---------- Revision Packs content ----------
const RevisionPacks = () => {
  const [idx, setIdx] = useState(0);

  // Preload all slides so transitions never wait on the network
  useEffect(() => {
    slides.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, []);

  const next = () => setIdx((i) => (i + 1) % slides.length);

  const bullets = [
    'Concise, exam-board aligned notes',
    '2 full mock papers with detailed worked solutions',
    'Practicals booklet with all the required practicals explained',
    '2-page cheat sheet for last minute revision',
  ];

  return (
    <div>
      {/* Hand-crafted A* hero */}
      <section className="pb-14 md:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-[42px] md:text-[64px] lg:text-[80px] leading-[1.0] tracking-tight font-semibold text-neutral-900"
          >
            Hand-crafted.<br />A* grade.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 md:mt-8 text-[16px] md:text-[18px] leading-[1.6] text-neutral-700 max-w-2xl mx-auto"
          >
            The same notes, exam practice and past papers I used to hit A* grades.
            Every page. Written by a med student. No fluff, no filler. A must-have for
            anyone aiming for the top.
          </motion.p>
        </div>
      </section>

      {/* Pack details + slideshow */}
      <section id="packs" className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Left column — pack details */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-display text-[30px] md:text-[40px] leading-[1.1] tracking-tight font-semibold text-neutral-900">
                A-Level Biology &amp; Chemistry<br />Revision Packs
              </h2>
              <p className="mt-5 text-[15px] md:text-[16px] leading-[1.65] text-neutral-700">
                Everything I used to secure top grades in A-Level Biology and Chemistry.
              </p>

              <ul className="mt-7 space-y-3 text-[15px] md:text-[16px] leading-[1.6] text-neutral-800">
                {bullets.map((b) => (
                  <li key={b} className="flex gap-3">
                    <span className="text-neutral-500 select-none">—</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="font-display text-[36px] md:text-[42px] font-semibold leading-none tracking-tight text-neutral-400 line-through decoration-[2px]">
                    £19.99
                  </div>
                  <div className="font-display text-[52px] md:text-[64px] font-semibold leading-none tracking-tight text-neutral-900">
                    £14.99
                  </div>
                </div>
                <div className="mt-2 text-[14px] text-neutral-600">One-time purchase</div>
                <div className="mt-3 text-[13px] md:text-[14px] font-medium leading-[1.5] text-red-700">
                  Launch discount: £14.99 for the first 50 students
                  <span className="block text-red-600/80 font-normal">(standard price: £19.99)</span>
                </div>
              </div>

              <motion.a
                href={KOFI_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="pill-btn mt-8 inline-flex items-center justify-center gap-2 bg-neutral-900 text-white px-7 py-3.5 text-[14px] font-medium hover:bg-neutral-700"
              >
                Buy Now on Ko-fi <ArrowRight size={16} />
              </motion.a>
            </motion.div>

            {/* Right column — slideshow (natural aspect, no white bars) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="w-full max-w-sm mx-auto md:mx-0 md:ml-auto"
            >
              <div className="relative w-full aspect-[5/7] rounded-3xl overflow-hidden shadow-[0_10px_50px_rgba(0,0,0,0.08)]">
                {slides.map((slide, i) => (
                  <motion.img
                    key={slide.src}
                    src={slide.src}
                    alt={slide.alt}
                    loading="eager"
                    decoding="async"
                    initial={false}
                    animate={{ opacity: i === idx ? 1 : 0 }}
                    transition={{ duration: 0.9, ease: 'easeInOut' }}
                    style={{ pointerEvents: i === idx ? 'auto' : 'none' }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ))}
              </div>

              {/* Indicators + next arrow */}
              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === idx ? 'w-8 bg-neutral-900' : 'w-3 bg-neutral-300 hover:bg-neutral-400'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={next}
                  aria-label="Next slide"
                  className="w-10 h-10 rounded-full border border-neutral-300 hover:border-neutral-500 hover:bg-white flex items-center justify-center transition-colors"
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ---------- YouTube content ----------
const YouTubeSection = () => (
  <section className="py-14 md:py-24 pb-24 md:pb-32 bg-white">
    <div className="max-w-5xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="grid md:grid-cols-2 gap-10 md:gap-14 items-center"
      >
        <motion.a
          href={YOUTUBE_URL}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="block rounded-3xl overflow-hidden aspect-[4/5] md:aspect-square"
        >
          <img
            src="/images/youtube-hamza.jpeg"
            alt="Hamza teaching Hess cycles on whiteboard"
            className="block w-full h-full object-cover"
          />
        </motion.a>

        <div>
          <div className="flex items-center gap-3">
            <YouTubeLogo size={32} />
            <span className="text-[12px] tracking-[0.22em] uppercase text-neutral-500">
              On YouTube
            </span>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 font-display text-[34px] md:text-[48px] leading-[1.05] tracking-tight font-semibold text-neutral-900"
          >
            Tired of textbooks?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-[15px] md:text-[17px] leading-[1.75] text-neutral-700"
          >
            Textbooks give you facts, but they don&apos;t teach you how to ace the exam.
            Over on <span className="font-semibold text-neutral-900">BioandChemMasters</span>,
            I swap dense pages for short, high-yield whiteboard tutorials that simplify
            the toughest GCSE and A-Level Biology and Chemistry concepts. Subscribe for
            the exact step-by-step strategies and mark-scheme secrets you need to secure
            top grades.
          </motion.p>

          <motion.a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="pill-btn mt-8 inline-flex items-center justify-center gap-2 bg-neutral-900 text-white px-7 py-3.5 text-[14px] font-medium"
          >
            <YouTubeLogo size={20} /> Watch on YouTube
          </motion.a>
        </div>
      </motion.div>
    </div>
  </section>
);

// ---------- Page ----------
const Resources = () => {
  const [selection, setSelection] = useState(null); // 'packs' | 'youtube' | null

  return (
    <>
      <TopIntro selection={selection} setSelection={setSelection} />

      <AnimatePresence mode="wait">
        {selection === 'packs' && (
          <motion.div
            key="packs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <RevisionPacks />
          </motion.div>
        )}
        {selection === 'youtube' && (
          <motion.div
            key="youtube"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <YouTubeSection />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Resources;
